import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

/**
 * Base API service providing common functionality for all API calls
 * Handles error handling, loading states, and request/response transformation
 */
@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private loadingSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public loading$: Observable<boolean> = this.loadingSubject.asObservable();

  constructor(private http: HttpClient) {}

  /**
   * Generic GET request with error handling
   */
  get<T>(endpoint: string): Observable<T> {
    this.setLoading(true);
    return this.http.get<T>(`${environment.apiBaseUrl}${endpoint}`).pipe(
      catchError((error) => this.handleError(error)),
      tap(() => this.setLoading(false))
    );
  }

  /**
   * Generic POST request with error handling
   */
  post<T>(endpoint: string, body: any): Observable<T> {
    this.setLoading(true);
    return this.http.post<T>(`${environment.apiBaseUrl}${endpoint}`, body).pipe(
      catchError((error) => this.handleError(error)),
      tap(() => this.setLoading(false))
    );
  }

  /**
   * Generic PUT request with error handling
   */
  put<T>(endpoint: string, body: any): Observable<T> {
    this.setLoading(true);
    return this.http.put<T>(`${environment.apiBaseUrl}${endpoint}`, body).pipe(
      catchError((error) => this.handleError(error)),
      tap(() => this.setLoading(false))
    );
  }

  /**
   * Generic DELETE request with error handling
   */
  delete<T>(endpoint: string): Observable<T> {
    this.setLoading(true);
    return this.http.delete<T>(`${environment.apiBaseUrl}${endpoint}`).pipe(
      catchError((error) => this.handleError(error)),
      tap(() => this.setLoading(false))
    );
  }

  /**
   * Handle HTTP errors and return user-friendly messages
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    let backendMessage: string | undefined;

    // Extract backend message from whichever shape the API returned it in
    if (error.error) {
      if (typeof error.error === 'string') {
        try {
          const parsed = JSON.parse(error.error);
          backendMessage = parsed?.message || parsed?.title;
        } catch {
          backendMessage = error.error;
        }
      } else {
        backendMessage =
          error.error?.message ||
          error.error?.title ||
          error.error?.detail;
      }
    }

    const fallbackMessage = `Server returned code ${error.status}`;
    let errorMessage = backendMessage || fallbackMessage;

    switch (error.status) {
      case 0:
        errorMessage = 'No connection to server';
        break;
      case 400:
        errorMessage = backendMessage || 'Bad request';
        break;
      case 401:
        errorMessage = backendMessage || 'Unauthorized';
        break;
      case 403:
        errorMessage = backendMessage || 'Forbidden';
        break;
      case 404:
        errorMessage = backendMessage || 'Not found';
        break;
      case 409:
        errorMessage = backendMessage || 'Conflict';
        break;
      case 422:
        errorMessage = backendMessage || 'Validation error';
        break;
      case 500:
        errorMessage = backendMessage || 'Server error';
        break;
      case 503:
        errorMessage = backendMessage || 'Service unavailable';
        break;
      default:
        errorMessage = backendMessage || fallbackMessage;
        break;
    }

    console.error('API Error FULL:', error);
    console.error('Extracted message:', errorMessage);

    this.setLoading(false);

    return throwError(() => new Error(errorMessage));
  }

  /**
   * Set loading state
   */
  private setLoading(loading: boolean): void {
    this.loadingSubject.next(loading);
  }

  /**
   * Get current loading state
   */
  public get isLoading(): boolean {
    return this.loadingSubject.value;
  }
}
